import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, RefreshCw, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchLeads, fetchLeadStats } from "@/lib/api";

export default function AdminLeads() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(t), fetchLeadStats(t)]);
      setLeads(l);
      setStats(s);
      setAuthed(true);
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setLoading(false);
    }
  };

  const badgeColor = (src) =>
    src === "dealer" ? "bg-brand-brown/15 text-brand-brown" : src === "contact" ? "bg-brand-blue/15 text-brand-blue" : "bg-brand-green/15 text-brand-green";

  if (!authed) {
    return (
      <main data-testid="admin-login" className="min-h-screen grid place-items-center bg-brand-ink px-5">
        <div className="w-full max-w-sm rounded-2xl bg-brand-cream p-8">
          <div className="grid place-items-center size-12 rounded-xl bg-brand-green text-white mb-5"><Lock className="size-5" /></div>
          <h1 className="font-heading font-bold text-2xl text-brand-ink">Leads Dashboard</h1>
          <p className="text-sm text-brand-ink/60 mt-1 mb-6">Enter the admin token to view enquiries.</p>
          <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="space-y-3">
            <Input data-testid="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" className="h-11 bg-white" />
            <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full rounded-full bg-brand-green text-white hover:bg-brand-greenDark h-11">
              {loading ? <Loader2 className="animate-spin" /> : "Unlock"}
            </Button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-brand-cream pt-24 pb-16">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="flex items-center justify-between">
          <h1 className="font-heading font-extrabold text-3xl text-brand-ink tracking-tight">Leads</h1>
          <Button variant="outline" onClick={() => load(token)} data-testid="admin-refresh" className="rounded-full gap-2"><RefreshCw className="size-4" /> Refresh</Button>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Total", value: stats.total },
              { label: "Contact", value: stats.by_source.contact },
              { label: "Enquiry", value: stats.by_source.enquiry },
              { label: "Dealer", value: stats.by_source.dealer },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-brand-ink/10 bg-white p-5">
                <div className="font-heading font-bold text-3xl text-brand-ink">{s.value}</div>
                <div className="text-xs font-mono uppercase tracking-wider text-brand-ink/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-brand-ink/10 bg-white overflow-hidden">
          {leads.length === 0 ? (
            <div className="p-16 text-center text-brand-ink/50">
              <Inbox className="size-8 mx-auto mb-3 opacity-40" /> No leads yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-cream text-brand-ink/60 font-mono text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Phone</th>
                    <th className="text-left px-5 py-3">Machinery</th>
                    <th className="text-left px-5 py-3">Source</th>
                    <th className="text-left px-5 py-3">Message</th>
                    <th className="text-left px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-brand-ink/5 hover:bg-brand-cream/50">
                      <td className="px-5 py-3 font-medium text-brand-ink">{l.name}<div className="text-xs text-brand-ink/50">{l.email || "—"}</div></td>
                      <td className="px-5 py-3 text-brand-ink/80">{l.phone}</td>
                      <td className="px-5 py-3 text-brand-ink/70">{l.machinery || "—"}</td>
                      <td className="px-5 py-3"><Badge className={`${badgeColor(l.source)} border-0 font-mono text-[10px] uppercase`}>{l.source}</Badge></td>
                      <td className="px-5 py-3 text-brand-ink/60 max-w-xs truncate">{l.message || "—"}</td>
                      <td className="px-5 py-3 text-brand-ink/50 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
